﻿import React, { useState, useEffect } from 'react';
import { FtirAnalysisData } from '../models/ftir-analysis-data';
import { DocumentType, FtirAnalysisType, FtirStandardType, MessageBoxReplyType, ModalMessageType } from '../models/enums';
import FtirStandardsComponent from './FtirStandardsComponent';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { MessageBoxReply } from '../models/message-box-reply';
import { ToastProps } from '../models/toast-props';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';

import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Item } from 'devextreme-react/form';
import { Toast } from 'devextreme-react/toast';

import DataGrid, {
    Column,
    Selection,
    Paging,
    Sorting,
    HeaderFilter,
    FilterRow,
    Button as ColumnButton,
    Editing,
    Popup,
    Form,
    Lookup
} from 'devextreme-react/data-grid';

let key = 1;

const DELETE_ANALYSIS_DATA_WARNING = 10;

const FTIR_STANDARD_TYPE_OPTIONS = [
    { label: 'Aurum', value: FtirStandardType.Aurum },
    { label: 'Aurum - Previous Lot', value: FtirStandardType.AurumPrevious },
    { label: 'United States Pharmacopia', value: FtirStandardType.USP },
    { label: 'European Pharmacopoeia', value: FtirStandardType.EP }
];

export interface Props {
    benchSheetId: string;
    ftirAnalysisType: FtirAnalysisType;
    isReadOnly: boolean;
    headerBgClass?: string;
    onSpectralDataChanged?: () => void;
}

export default function SpectralFilesComponent(props: Props) {
    const [ftirAnalysisData, setFtirAnalysisData] =
        useState<FtirAnalysisData[]>();

    const [selectedFtirAnalysisData, setSelectedFtirAnalysisData] =
        useState<FtirAnalysisData>();

    const [solventStandardSearch, setSolventStandardSearch] =
        useState<DocumentType>();

    const [ftirStandardsEditDialogVisible, setFtirStandardsEditDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    key++;

    return (
        <div>
            <div className={`lq-header pw-margin-top-05 ${props.headerBgClass ? props.headerBgClass : 'pw-bg-rb-700'}`}>
                <div className="lq-header-title">Spectral Image Files</div>
                <div className="title-bar-spacer" />

                <div className="save-toolbar">
                    <div>
                        <Button className="title-bar-button"
                            text="" icon="plus" type="normal"
                            hint="add standard"
                            onClick={addStandard} />
                    </div>
                </div>
            </div>

            <div className="pw-comp-row">
                <DataGrid className="pw-no-max-width"
                    height="10rem"
                    width="100%"
                    keyExpr="ftirAnalysisDataId"
                    dataSource={ftirAnalysisData ? ftirAnalysisData : []}
                    onSelectionChanged={ftirStandardDatagridSelectionChanged}
                    onRowValidating={onFtirAnalysisDataRowValidating}
                    onRowRemoving={onFtirAnalysisDataRowDeleting}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    wordWrapEnabled={true}
                    showBorders={true}>

                    <Sorting mode="none" />
                    <Selection mode={'single'} />
                    <Paging enabled={false} />

                    <Column type="buttons" width='8rem' >
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={showFtirStandardAuditDialog} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onFtirStandardInformationButtonClicked} />
                    </Column>

                    <Column dataField='ftirStandardTypeId' caption="Std Manufacturer" dataType="string" width='12rem'>
                        <Lookup dataSource={FTIR_STANDARD_TYPE_OPTIONS ? FTIR_STANDARD_TYPE_OPTIONS : []}
                            allowClearing={false} displayExpr='label' valueExpr='value' />
                    </Column>

                    <Column dataField='catalogNumber' caption="Catalog #" dataType="string" width='18rem' />
                    <Column dataField='lotNumber' caption="Lot #" dataType="string" width='16rem' />

                    {/*
                    {props.ftirAnalysisType !== FtirAnalysisType.AsIs &&
                        <>
                            <Column dataField='solvent' dataType="string" width='16rem' />
                            <Column dataField='chemicalId' caption="Chemical ID" dataType="string" width='12rem' />
                            <Column dataField='chemicalName' dataType="string" width='16rem' />
                        </>
                    }
                    */}

                    <Column />

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowAdding={false}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly}>

                        <Popup wrapperAttr={{ id: 'editFtirAnalysisDataPopup' }} title="Edit FTIR Analysis Data"
                            hideOnOutsideClick={false} showTitle={true} width={700} height={380} />
                        <Form>
                            <Item dataField="ftirStandardTypeId"
                                caption="Std Manufacturer" colSpan={2} isRequired={true} />
                            <Item dataField="catalogNumber" caption="Catalog #" />
                            <Item dataField="lotNumber" caption="Lot #" />

                            {/*
                            {props.ftirAnalysisType !== FtirAnalysisType.AsIs &&
                                <>
                                    <Item dataField="solvent" />
                                    <Item dataField="chemicalId" caption="Chemical ID" />
                                    <Item dataField="chemicalName" />
                                </>
                            }
                            */}
                        </Form>
                    </Editing>

                </DataGrid>
            </div>

            {ftirStandardsEditDialogVisible &&
                <DialogPopup key={`fs${key}`}
                    fullScreen={true}
                    dragEnabled={false}
                    visible={ftirStandardsEditDialogVisible}
                    onHiding={hideFtirStandardsEditDialog}
                    title="Edit FTIR Standards" >
                    <FtirStandardsComponent benchSheetId={props.benchSheetId}
                        ftirStandardTypeOptions={FTIR_STANDARD_TYPE_OPTIONS}
                        ftirAnalysisType={props.ftirAnalysisType}
                        onClose={onFtirStandardsComponentClose} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata}
                    onReply={onGlobalDialogReply} onClose={onGlobalDialogClose} />
            }
        </div>
    );

    function onGlobalDialogReply(reply: MessageBoxReply) {
        if (reply.code === DELETE_ANALYSIS_DATA_WARNING) {
            clearGlobalDialog();

            if (reply.reply !== MessageBoxReplyType.Yes)
                return;

            onDeleteFtirAnalysisDataCallback();
        }
    }

    function onGlobalDialogClose() {
        clearGlobalDialog();
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

    function addStandard() {
        setFtirStandardsEditDialogVisible(true);
    }

    function ftirStandardDatagridSelectionChanged(e: any) {
        //needed??
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedFtirAnalysisData(e.selectedRowsData[0])
    }

    async function onFtirAnalysisDataRowValidating(e: any) {
        if (e.oldData) {
            let ftirAnalysisData = e.oldData as FtirAnalysisData;

            if (ftirAnalysisData) {
                let updatedFtirAnalysisData = { ...ftirAnalysisData, ...e.newData };

                updateFtirAnalysisData(updatedFtirAnalysisData)
                    .then(() => {
                        showToast("FTIR Analysis Data Updated", "success");
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating FTIR Analysis Data");
                    });
            }
        }
    }

    async function onFtirAnalysisDataRowDeleting(e: any) {
        setSelectedFtirAnalysisData(e.data);
        e.cancel = true;

        showPromptMessage(ModalMessageType.PromptYesNo,
            "FTIR Analysis Data Deletion",
            "This operation will delete the Analysis Data and all of its spectral points. This operation cannot be undone. Do you want to proceed?",
            DELETE_ANALYSIS_DATA_WARNING);
    }

    async function onDeleteFtirAnalysisDataCallback() {
        if (selectedFtirAnalysisData) {
            let ftirAnalysisDataId = selectedFtirAnalysisData.ftirAnalysisDataId;

            await deleteFtirAnalysisData(ftirAnalysisDataId)
                .then(() => {
                    showToast("FTIR Analysis Data Deleted", "success");
                    getFtirStandards();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting FTIR Analysis Data");
                });
        }
    }

    //show/hide

    async function getFtirStandards() {
        await CrudFunctions.getData(`benchsheets/ftiranalysisdata/${props.benchSheetId}/${props.ftirAnalysisType}`)
            .then((data) => {
                setFtirAnalysisData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving FTIR Analysis Data");
            });
    }

    //TODO
    async function updateFtirAnalysisData(updatedAnalysisData: FtirAnalysisData) {
        return await CrudFunctions.putData("benchsheets/ftiranalysisdata", updatedAnalysisData);
    }

    // DELETE functions
    async function deleteFtirAnalysisData(ftirAnalysisDataId: string) {
        return await CrudFunctions.deleteData(`benchsheets/ftiranalysisdata/${ftirAnalysisDataId}`);
    }

    async function getAllData() {
        await Promise.all([
            getFtirStandards()
        ])
    }

    function onFtirStandardsComponentClose() {
        setFtirStandardsEditDialogVisible(false);

        getFtirStandards();
    }

    function showFtirStandardAuditDialog(e: any) {

    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function onFtirStandardInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const ftirAnalysisData = e.row.data as FtirAnalysisData;

            if (ftirAnalysisData) {
                setSelectedFtirAnalysisData(ftirAnalysisData);

                let itemInformation: ItemBasicInformation = {
                    rowPointer: ftirAnalysisData.ftirAnalysisDataId,
                    createTimestamp: ftirAnalysisData.createTimestamp,
                    lastModifiedTimestamp: ftirAnalysisData.modifyTimestamp,
                    createUser: ftirAnalysisData.createUser,
                    modifyUser: ftirAnalysisData.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideFtirStandardsEditDialog() {
        setFtirStandardsEditDialogVisible(false);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showPromptMessage(mode: ModalMessageType, title: string, message: string, code: number) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message,
            code: code
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}
